import * as React from 'react';
import { connect } from 'react-redux';
import { groupBy, orderBy } from 'lodash';

import PageHeading from 'components/page/page-heading/PageHeading';
import { Table, TableContainer } from 'components/table';
import ActionMessage from 'components/action-message/ActionMessage';
import Spinner from 'components/spinner/Spinner';

import UserNotificationsRowsGrouped from '../UserNotificationsRowsGrouped';

import {
  loadNotifications,
  resetNotifications,
  updateNotificationSettingsGroup,
  updateNotificationSettingsSingle
} from '../../../reducers/userNotifications';

import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./user-notifications.i18n.json'));

import type {
  Account,
  UserNotification,
  UserNotificationType,
  UserNotificationUserMeta
} from 'types';

type NotificationsListByState = {
  preparation: Array<UserNotification>,
  active: Array<UserNotification>,
  inactive: Array<UserNotification>
};

type OwnProps = {
  title: string | undefined | null
};

type StateProps = {
  currentAccount: Account,
  notifications: Array<UserNotification>,
  collectionNotifications: UserNotificationUserMeta,
  isLoading: boolean
};

type DispatchProps = {
  dispatchLoadNotifications: (a: number) => void,
  dispatchResetNotifications: () => void,
  dispatchUpdateNotificationSettingsSingle: (
    notificationId: number,
    allowNotifications: boolean,
    type: UserNotificationType
  ) => void,
  dispatchUpdateNotificationSettingsGroup: (
    notifications: Array<UserNotification>,
    collectionNotifications: UserNotificationUserMeta,
    type: UserNotificationType
  ) => void
};

type Props = OwnProps & StateProps & DispatchProps;

class UserNotifications extends React.PureComponent<Props> {
  componentDidMount(): void {
    const { currentAccount, dispatchLoadNotifications } = this.props;

    dispatchLoadNotifications(currentAccount.id);
  }

  componentWillUnmount(): void {
    const { dispatchResetNotifications } = this.props;

    dispatchResetNotifications();
  }

  getSortedNotificationsByCampaignState(notifications: Array<UserNotification>): Array<UserNotification> {
    const notificationsListByState: NotificationsListByState = {
      preparation: [],
      active: [],
      inactive: []
    };

    if (notifications.length === 0) {
      return notifications;
    }

    notifications.forEach((notification) => {
      const { campaign } = notification;

      notificationsListByState[campaign.state] = notificationsListByState[campaign.state] || [];
      notificationsListByState[campaign.state].push(notification);
      notificationsListByState[campaign.state] = orderBy(
        notificationsListByState[campaign.state],
        [
          (n) => n.campaign.name.toLowerCase()
        ]
      );
    });

    return [
      ...notificationsListByState.preparation,
      ...notificationsListByState.active,
      ...notificationsListByState.inactive
    ];
  }

  handleSaveGroupSetting = (
    notifications: Array<UserNotification>,
    collectionNotifications: UserNotificationUserMeta,
    type: UserNotificationType
  ): void => {
    const { dispatchUpdateNotificationSettingsGroup } = this.props;

    dispatchUpdateNotificationSettingsGroup(notifications, collectionNotifications, type);
  };

  handleSaveNotificationSetting = (
    notificationId: number,
    allowNotifications: boolean,
    type: UserNotificationType
  ): void => {
    const { dispatchUpdateNotificationSettingsSingle } = this.props;

    dispatchUpdateNotificationSettingsSingle(notificationId, allowNotifications, type);
  };

  renderNotifications(sortedNotifications: Array<UserNotification>): React.ReactNode {
    const { collectionNotifications } = this.props;
    const groupedNotifications = groupBy(sortedNotifications, 'key');

    return (
      <UserNotificationsRowsGrouped
        groupedNotifications={groupedNotifications}
        collectionNotifications={collectionNotifications}
        onNotificationChange={this.handleSaveNotificationSetting}
        onGroupChange={this.handleSaveGroupSetting}
      />
    );
  }

  render(): React.ReactNode {
    const { notifications, isLoading, title } = this.props;

    if (isLoading) {
      return (
        <section>
          <PageHeading
            color="light"
            heading={title || __t('user_notifications.my_notifications_settings')}
          />
          <div className="user-notifications__spinner">
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          </div>
        </section>
      );
    }

    const sortedNotifications = this.getSortedNotificationsByCampaignState(notifications);

    return (
      <section>
        <PageHeading
          color="light"
          heading={title || __t('user_notifications.my_notifications_settings')}
          margin="top-none"
        />
        {
          notifications.length === 0 ?
            <ActionMessage
              message={__t('user_notifications.email_notifications_empty')}
              size="normal"
            /> :
            <TableContainer>
              <Table>
                {this.renderNotifications(sortedNotifications)}
              </Table>
            </TableContainer>
        }
      </section>
    );
  }
}

const mapStateToProps = ({ userNotifications, account }): StateProps => ({
  currentAccount: account.currentAccount,
  notifications: userNotifications.notifications,
  collectionNotifications: userNotifications.collectionNotifications,
  isLoading: userNotifications.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchLoadNotifications: (userId: number) => (
    dispatch(loadNotifications(userId))
  ),

  dispatchResetNotifications: () => (
    dispatch(resetNotifications())
  ),

  dispatchUpdateNotificationSettingsSingle: (
    notificationId: number,
    allowNotifications: boolean,
    type: UserNotificationType
  ) => (
    dispatch(updateNotificationSettingsSingle(notificationId, allowNotifications, type))
  ),

  dispatchUpdateNotificationSettingsGroup: (
    notifications: Array<UserNotification>,
    collectionNotifications: UserNotificationUserMeta,
    type: UserNotificationType
  ) => (
    dispatch(updateNotificationSettingsGroup(notifications, collectionNotifications, type))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(UserNotifications);
